import type { ReactElement } from 'react';

import { IconButton } from '@/ui/components/IconButton';
import type { IconName } from '@/ui/components/iconName';
import type { ThemePreference } from '@/services/types';

import { nextThemePreference, useTheme } from './ThemeProvider';

/**
 * The theme control — docs/DESIGN.md §7.5, recon §1.2, ADR-0017, ADR-0101 §7.3.
 *
 * ═══ THREE STATES, NOT TWO ═══
 *
 * light → dark → system → light. The legacy toggle flipped between two, which made
 * "follow the OS" unreachable once the user had pressed the button even once — and since the
 * legacy default was `dark` regardless of `prefers-color-scheme`, every light-theme user
 * pressed it at least once. `system` is a state you can get back to here.
 *
 * ═══ THE LABEL AND THE GLYPH BOTH NAME THE NEXT STATE, NOT THE CURRENT ONE ═══
 *
 * docs/DESIGN.md §7.5 spells this out and it is worth being precise about why. A button
 * labelled with its current state ("Dark theme") is a STATUS; a screen-reader user hears it
 * and has no idea what pressing it does, and two users can reasonably read it in opposite
 * directions. A button labelled with its effect ("Switch to the dark theme") is an ACTION,
 * which is what a `<button>` is.
 *
 * ADR-0100 then drew the CURRENT state as the glyph, so the two channels disagreed: a moon
 * beside the name "Switch to the light theme". The judges of ADR-0101 caught it, and the glyph
 * now shows the destination too. The page itself already says which theme is in force — it is
 * the one you are looking at — so the only thing the control has to say is what it will do,
 * and it now says that once, twice over. The CURRENT preference is still published, as
 * `data-theme-preference`, for anything that needs to read it.
 *
 * That is also why `labels` is keyed by the DESTINATION and not by the current preference:
 * the caller cannot wire it up backwards without the key names reading as obviously wrong.
 */

export interface ThemeToggleLabels {
  /** `action.themeToLight` — shown when the NEXT press selects the light theme. */
  readonly toLight: string;
  /** `action.themeToDark`. */
  readonly toDark: string;
  /** `action.themeToSystem`. */
  readonly toSystem: string;
}

export interface ThemeToggleProps {
  /** Already localised. `ui/**` does not translate (ADR-0003, ADR-0018). */
  readonly labels: ThemeToggleLabels;
  readonly className?: string;
}

/** The glyph of each preference. `system` gets the display, not a sun or a moon. */
const ICON: Record<ThemePreference, IconName> = {
  light: 'sun',
  dark: 'moon',
  system: 'monitor',
};

const LABEL_OF_DESTINATION: Record<ThemePreference, keyof ThemeToggleLabels> = {
  light: 'toLight',
  dark: 'toDark',
  system: 'toSystem',
};

export function ThemeToggle({ labels, className }: ThemeToggleProps): ReactElement {
  const { preference, cycle } = useTheme();
  const destination = nextThemePreference(preference);

  return (
    <IconButton
      // Tinted peri, the neutral family: the theme is a preference, not a verdict on the JSON.
      variant="tinted"
      hue="peri"
      // The DESTINATION's glyph, so the picture and the name say the same thing.
      icon={ICON[destination]}
      label={labels[LABEL_OF_DESTINATION[destination]]}
      onClick={cycle}
      className={className}
      // The current state, for anything that needs to assert on it — the e2e suite, and a
      // user's own stylesheet. `aria-label` already carries the meaning for assistive tech,
      // so this attribute is not doing accessibility work and must not start.
      data-theme-preference={preference}
    />
  );
}

export default ThemeToggle;
